import React, { useState, useEffect } from "react";
import { LOGO } from "@/lib/assets";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Search, Heart } from "lucide-react";
import { Button } from "@/components/ui/button";

const LINKS = [
  { to: "/marketplace", label: "Marketplace", testid: "navbar-link-marketplace" },
  { to: "/housing", label: "Housing", testid: "navbar-link-housing" },
  { to: "/financial-wellness", label: "Financial Wellness", testid: "navbar-link-financial" },
  { to: "/grants", label: "Grants & Funding", testid: "navbar-link-grants" },
  { to: "/events", label: "Events", testid: "navbar-link-events" },
  { to: "/about", label: "About", testid: "navbar-link-about" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => setOpen(false), [pathname]);

  const solid = scrolled || pathname !== "/";
  const textCls = solid ? "text-navy-800" : "text-white";

  if (pathname === "/admin") return null;

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
        solid ? "glass-nav bg-white/90 border-b border-slate-200/70 shadow-sm" : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 h-20 flex items-center justify-between">
        <Link to="/" data-testid="navbar-brand-logo" className="flex items-center gap-3 group">
          <img src={LOGO} alt="Millionaire Mindsetters" className={`h-9 sm:h-10 w-auto object-contain transition-all duration-500 ${solid ? "invert" : ""}`} />
          <div className={`text-[10px] uppercase tracking-[0.25em] font-semibold ${solid ? "text-gold" : "text-gold-light"}`}>Foundation</div>
        </Link>

        <div className="hidden lg:flex items-center gap-7">
          {LINKS.map((l) => (
            <Link key={l.to} to={l.to} data-testid={l.testid}
              className={`text-sm font-medium link-underline ${textCls} hover:opacity-80`}>
              {l.label}
            </Link>
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-3">
          <Link to="/volunteer" data-testid="navbar-cta-volunteer">
            <Button variant="ghost" className={`rounded-full ${solid ? "text-navy-800 hover:bg-slate-100" : "text-white hover:bg-white/10"}`}>Volunteer</Button>
          </Link>
          <Link to="/donate" data-testid="navbar-cta-donate">
            <Button className="rounded-full bg-gold hover:bg-gold-light text-white font-semibold px-5 shadow-lg shadow-gold/20">
              <Heart className="h-4 w-4 mr-1.5" /> Donate
            </Button>
          </Link>
        </div>

        <button className={`lg:hidden ${textCls}`} onClick={() => setOpen(!open)} data-testid="navbar-mobile-toggle" aria-label="Menu">
          {open ? <X /> : <Menu />}
        </button>
      </nav>

      {open && (
        <div className="lg:hidden bg-white border-t border-slate-200 px-6 py-5 space-y-3 animate-fade-in">
          {LINKS.map((l) => (
            <Link key={l.to} to={l.to} className="block py-2 text-navy-800 font-medium" data-testid={`mobile-${l.testid}`}>{l.label}</Link>
          ))}
          <div className="flex gap-3 pt-3">
            <Link to="/volunteer" className="flex-1"><Button variant="outline" className="w-full rounded-full">Volunteer</Button></Link>
            <Link to="/donate" className="flex-1"><Button className="w-full rounded-full bg-gold hover:bg-gold-light text-white">Donate</Button></Link>
          </div>
        </div>
      )}
    </header>
  );
};
